<template>
    <DesktopEmulator v-if="isDesktop && !route.meta.allowDesktop && !isInIframe" />
    <template v-else>
        <main class="main-container">
            <router-view v-slot="{ Component, route }">
                <transition :name="route.meta.transition || 'none'">
                    <div :key="route.name" class="page-wrapper">
                        <keep-alive>
                            <component
                                :is="Component"
                                v-if="route.meta.keepAlive"
                            />
                        </keep-alive>
                        <component
                            :is="Component"
                            v-if="!route.meta.keepAlive"
                        />
                    </div>
                </transition>
            </router-view>
        </main>

        <NotificationManager v-if="authStore.userId" />
        <CallManager v-if="authStore.userId" />
        <BuyStarsModal v-if="showBuyStarsModal" v-model="showBuyStarsModal" />
        <GenericModal
            v-model="modalStore.state.showModal"
            :darkMode="modalStore.state.props.darkMode"
            :fullscreenMobile="true"
        >
            <!-- Any content can go here, completely independent of the app's styling -->
            <div class="modal-content-container">
                <Continue2 v-bind="modalStore.state.props" />
            </div>
        </GenericModal>

        <!-- Payment Success Handler -->
        <PaymentSuccessHandler />
    </template>
</template>

<style>
.main-container {
    position: relative;
    width: 100%;
    height: 100%;
}

.page-wrapper {
    width: 100%;
    height: 100%;
}

.slide-up-enter-active,
.slide-down-enter-active {
    transition: transform 0.4s ease-out;
}

.slide-up-leave-active,
.slide-down-leave-active {
    transition: transform 0.4s ease-in;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
}

.slide-up-enter-from {
    transform: translateY(100%);
}

.slide-up-leave-to {
    transform: translateY(-100%);
}

.slide-down-enter-from {
    transform: translateY(-100%);
}

.slide-down-leave-to {
    transform: translateY(100%);
}

.none-enter-active,
.none-leave-active {
    transition: none;
}

.modal-content-container {
    margin-top: 60px;
}

.modal-content-container {
    margin-top: 60px;
}

/* Global styles removed and shifted to JS injection for reliable iframe styling */
</style>

<script setup>
// Vue Router
import { RouterView, useRoute, useRouter } from 'vue-router'
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

// Firebase
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'

// Stores
import { useAuthStore } from '@/stores/auth.store'
import { usePaymentStore } from '@/stores/payment.store'
import { useModalStore } from '@/stores/modal.store'
const modalStore = useModalStore()

// Services
import {
    initializeAppSockets,
    cleanupAppSockets,
} from '@/services/socket/socketInitializer.service.js'
import UserService from '@/services/api/user.service.js'

// Components
import { defineAsyncComponent } from 'vue'
import BuyStarsModal from '@/components/general-modals/add-funds.vue'
import GenericModal from '@/components/shared/core/GenericModal.vue'
import Continue2 from '@/components/authentication/continue2.vue'
import DesktopEmulator from '@/components/shared/DesktopEmulator.vue'

const NotificationManager = defineAsyncComponent(
    () => import('@/components/NotificationManager.vue')
)
const CallManager = defineAsyncComponent(
    () => import('@/components/calls/CallManager.vue')
)
const PaymentSuccessHandler = defineAsyncComponent(
    () => import('@/components/payment/PaymentSuccessHandler.vue')
)

// Utils
import { performLogoutCleanup } from '@/utils/auth.js'
import {
    safeSessionStorageGet,
    safeSessionStorageSet,
} from '@/utils/storage.js'

// Initialize stores
const authStore = useAuthStore()
const paymentStore = usePaymentStore()
const router = useRouter()
const route = useRoute()
const { locale } = useI18n()

// Desktop Detection
const isDesktop = ref(false)
const checkDesktop = () => {
    isDesktop.value = window.innerWidth >= 768
}

const isInIframe = ref(false)
try {
    isInIframe.value = window.self !== window.top
} catch (e) {
    isInIframe.value = true // Assume we're in an iframe if we can't access window.top (cross-origin)
}

// If we are in the emulator iframe, post our route changes to the parent
watch(() => route.fullPath, (newPath) => {
    if (isInIframe.value) {
        window.parent.postMessage({
            type: 'SANTINA_ROUTE_CHANGE',
            path: newPath
        }, '*')
    }
})

// Sync language changes from iframe to parent emulator
watch(locale, (newLocale) => {
    if (isInIframe.value) {
        window.parent.postMessage({
            type: 'SANTINA_LANGUAGE_CHANGE',
            locale: newLocale
        }, '*')
    }
})

// Listen for navigation messages from the parent emulator
const handleParentNavigation = (event) => {
    if (event.data && event.data.type === 'SANTINA_NAVIGATE') {
        const newPath = event.data.path
        if (newPath !== route.fullPath) {
            router.replace(newPath)
        }
    }
}

onMounted(() => {
    if (isInIframe.value) {
        window.addEventListener('message', handleParentNavigation)
        document.documentElement.classList.add('is-emulated-iframe')
        
        // Inject global scrollbar style dynamically to ensure it overrides everything else
        const style = document.createElement('style')
        style.type = 'text/css'
        style.innerHTML = `
            /* Hide scrollbar for Chrome, Safari and Opera */
            ::-webkit-scrollbar {
                display: none !important;
            }
            /* Hide scrollbar for IE, Edge and Firefox */
            * {
                -ms-overflow-style: none !important;  /* IE and Edge */
                scrollbar-width: none !important;  /* Firefox */
            }
        `
        document.head.appendChild(style)
    }
})

onUnmounted(() => {
    window.removeEventListener('resize', checkDesktop)
    if (isInIframe.value) {
        window.removeEventListener('message', handleParentNavigation)
    }
})

// Initialization state tracking
const initState = ref({
    initialized: false,
    initializing: false,
    lastAuthTime: null,
    lastAuthUid: null,
})
const lastKnownAuthUid = ref(null)

router.beforeEach((to, from) => {
    const toDepth = to.meta.order
    const fromDepth = from.meta.order

    if (toDepth === undefined || fromDepth === undefined) {
        to.meta.transition = 'none'
        return
    }

    if (toDepth > fromDepth) {
        to.meta.transition = 'slide-up'
    } else if (toDepth < fromDepth) {
        to.meta.transition = 'slide-down'
    } else {
        to.meta.transition = 'none'
    }
})

// Throttle function to prevent multiple rapid executions
const throttleInitialization = (user) => {
    // Get current time
    const now = Date.now()

    // If we're currently initializing or have initialized for this user, skip
    if (initState.value.initializing) {
        console.log(
            'Initialization already in progress, skipping duplicate auth event'
        )
        return false
    }

    // If this is the same user and it's been less than 5 seconds AND initialization was successful, skip (likely token refresh)
    if (
        user &&
        initState.value.lastAuthUid === user.uid &&
        initState.value.lastAuthTime &&
        now - initState.value.lastAuthTime < 5000 &&
        initState.value.initialized
    ) {
        console.log('Skipping rapid auth state change - likely token refresh')
        return false
    }

    // Update last auth time and uid
    initState.value.lastAuthTime = now
    initState.value.lastAuthUid = user?.uid || null

    // Allow initialization to proceed
    return true
}

// Main app initialization function
const initializeApp = async (user) => {
    // Prevent concurrent initializations
    if (initState.value.initializing) return

    initState.value.initializing = true
    authStore.setAppInitialized(false)

    try {
        console.log('Starting app initialization for user:', user?.uid)

        // 1. Fetch user data from backend
        const creationTime =
            user.metadata?.creationTime || user.metadata?.createdAt || null
        const lastSignInTime =
            user.metadata?.lastSignInTime || user.metadata?.lastLoginAt || null

        console.log(
            'User metadata times:',
            'creationTime =',
            creationTime,
            'lastSignInTime =',
            lastSignInTime
        )

        const isNewUser =
            creationTime && lastSignInTime && creationTime === lastSignInTime

        if (isNewUser) {
            console.log('New user detected, polling backend for user data...')

            // Try to get user data with retries
            let userDataFetched = false
            let attempts = 0
            const maxAttempts = 14

            while (!userDataFetched && attempts < maxAttempts) {
                // Bail out if the Firebase user was signed out (e.g. backend
                // rejected the signup with DEVICE_BLOCKED and the orphaned
                // anonymous user was cleaned up)
                if (!firebase.auth().currentUser) {
                    console.log(
                        'Firebase user signed out during polling, aborting.'
                    )
                    return
                }

                attempts++
                console.log(
                    `Attempt ${attempts}/${maxAttempts} to fetch user data...`
                )

                try {
                    const response = await UserService.getCurrentUser()

                    if (response.status === 200 && response.data) {
                        console.log('User data found in backend!')
                        userDataFetched = true
                        // Set the data directly to avoid an extra API call
                        authStore.user.data = response.data
                        break
                    } else {
                        console.log(
                            `User not ready in backend yet (status: ${response.status})`
                        )
                        // Wait 1 second before next attempt
                        await new Promise((resolve) =>
                            setTimeout(resolve, 1000)
                        )
                    }
                } catch (error) {
                    console.log('Error fetching user data:', error)
                    // Wait 1 second before next attempt
                    await new Promise((resolve) => setTimeout(resolve, 1000))
                }
            }

            if (!userDataFetched) {
                console.warn(
                    `Failed to fetch user data after ${maxAttempts} attempts.`
                )
                throw new Error('Failed to fetch user data from backend')
            }
        } else {
            // Existing user - reuse data if already present
            let userData = authStore.user.data

            if (userData) {
                console.log(
                    'Existing user data already available, skipping fetch'
                )
            } else {
                const maxAttempts = 5
                let attempts = 0

                while (attempts < maxAttempts && !userData) {
                    attempts++
                    console.log(
                        `Existing user fetch attempt ${attempts}/${maxAttempts}`
                    )

                    try {
                        const response = await UserService.getCurrentUser()
                        if (response.status === 200 && response.data) {
                            userData = response.data
                            authStore.user.data = response.data
                        } else {
                            userData = null
                        }
                    } catch (err) {
                        console.warn('Direct getCurrentUser failed:', err)
                        userData = null
                    }

                    if (!userData) {
                        await new Promise((resolve) => setTimeout(resolve, 600))
                    }
                }

                if (!userData) {
                    throw new Error('Failed to fetch user data from backend')
                }
            }
        }

        // 2. Check if user data was successfully loaded
        if (!authStore.user.data) {
            console.error('User data is still null after fetch attempts')
            throw new Error('Failed to load user data from backend')
        }

        // NOTE: Email verification for creators is temporarily disabled
        // Uncomment the block below to re-enable
        // 3. Check if Creator user needs email verification (email/password auth only)
        // if (authStore.user.data?.typeofuser === 'Creator') {
        //     const usedEmailPassword = user.providerData.some(
        //         (provider) => provider.providerId === 'password'
        //     )
        //
        //     if (usedEmailPassword && !user.emailVerified) {
        //         console.log(
        //             'Creator authenticated with email/password but email not verified. Redirecting to email-verification page.'
        //         )
        //
        //         // Only redirect if not already on the verification page
        //         if (router.currentRoute.value.name !== 'email-verification') {
        //             await router
        //                 .push({ name: 'email-verification' })
        //                 .catch((err) => {
        //                     console.error(
        //                         'Failed to redirect to email verification:',
        //                         err
        //                     )
        //                 })
        //         }
        //         return
        //     }
        // }

        console.log('User data loaded, proceeding with socket initialization')

        // 4. Get ID Token for socket initialization (without forcing a refresh)
        const idToken = await user.getIdToken(false)

        // 5. Initialize app sockets
        await initializeAppSockets(idToken)

        // 6. Close modals (unless the auth modal needs to stay open for redirect loading)
        if (!modalStore.state.props?.keepOpenOnAuth) {
            modalStore.closeModal()
        }

        console.log('App initialization completed successfully')
        initState.value.initialized = true
        authStore.setAppInitialized(true)

        // 7. Check if user needs to complete onboarding steps for creator users
        if (authStore.user.data?.typeofuser === 'Creator') {
            const { checkOnboardingRedirect } = await import(
                '@/utils/onboardingCheck.js'
            )
            const onboardingRoute = checkOnboardingRedirect(
                authStore.user,
                router.currentRoute.value.name
            )

            if (onboardingRoute) {
                console.log(
                    'User has incomplete onboarding, redirecting to:',
                    onboardingRoute.name
                )
                router.push(onboardingRoute)
            }
        }
    } catch (error) {
        console.error('Error during app initialization:', error)
        // Don't automatically logout on initialization error
    } finally {
        initState.value.initializing = false
    }
}

// Handle splash screen on initial load
onMounted(() => {
    // Check for desktop environment
    checkDesktop()
    window.addEventListener('resize', checkDesktop)

    // Hide inline splash screen (from index.html) now that Vue has mounted
    const inlineSplash = document.getElementById('inline-splash')
    if (inlineSplash) {
        inlineSplash.style.pointerEvents = 'none'
        // Ensure initial state is set before transition
        inlineSplash.style.transition = 'opacity 0.5s ease-out'
        inlineSplash.style.opacity = '1'

        // Trigger reflow/next frame to ensure transition plays
        requestAnimationFrame(() => {
            inlineSplash.style.opacity = '0'
        })
        
        setTimeout(() => {
            inlineSplash.remove()
        }, 500)
    }

    // Capture AdCash click ID from URL parameters (users arrive here from AdCash campaigns)
    const urlParams = new URLSearchParams(window.location.search)
    const adcashClickId = urlParams.get('cid') || urlParams.get('clickid')
    if (adcashClickId) {
        localStorage.setItem('adcashClickId', adcashClickId)
        console.log('AdCash click ID captured on landing page:', adcashClickId)
    }

    // Set session start flag
    safeSessionStorageSet('sessionStart', 'true')
})

// Handle auth state changes
firebase.auth().onAuthStateChanged(async (user) => {
    try {
        console.log(
            'Firebase Auth State Changed: user=',
            user ? user.uid : 'null'
        )

        authStore.setUser(user)

        const hadAuthenticatedUser = lastKnownAuthUid.value !== null
        lastKnownAuthUid.value = user?.uid ?? null

        if (!user) {
            // User is logged out, perform cleanup and redirect
            console.log('User is logged out. Performing cleanup and redirect.')
            initState.value.initialized = false // Reset initialization state
            authStore.setAppInitialized(false)
            if (hadAuthenticatedUser) {
                await performLogoutCleanup()
            } else {
                console.log('Skipping cleanup – no authenticated session yet.')
            }

            // If we landed with creator attribution query params on a public route, go to creator landing.
            try {
                const current = router.currentRoute.value
                const hasCreatorLandingParams = Boolean(
                    current?.query?.invite ||
                        current?.query?.studioInvite ||
                        current?.query?.partner ||
                        current?.query?.partnerUsername ||
                        current?.query?.new ||
                        current?.query?.code
                )
                if (
                    hasCreatorLandingParams &&
                    (current?.name === 'auth' || current?.meta?.noAuth)
                ) {
                    router.push({ name: 'creator-home', query: current.query })
                    return
                }
            } catch (_) {}

            // Check for a stored post-logout redirect target (e.g. onboarding exit)
            try {
                const storedRedirect = sessionStorage.getItem('postLogoutRedirect')
                if (storedRedirect) {
                    const parsedRedirect = JSON.parse(storedRedirect)
                    sessionStorage.removeItem('postLogoutRedirect')
                    await router.push(parsedRedirect).catch((err) => {
                        console.error('Post-logout redirect failed:', err)
                    })
                    return
                }
            } catch (e) {
                console.warn('Failed to handle post-logout redirect:', e)
            }

            // Redirect to auth page, unless already on a no-auth page
            if (!router.currentRoute.value.meta.noAuth) {
                console.log(
                    'User logged out, redirecting to auth page from:',
                    router.currentRoute.value.path
                )
                await router.push({ name: 'auth' }).catch((err) => {
                    console.error('Router push failed:', err)
                })
                console.log(
                    'Navigation complete, new route:',
                    router.currentRoute.value.path
                )
            } else {
                console.log(
                    'User logged out but already on noAuth page, skipping redirect'
                )
            }
            return
        }

        // Only initialize if this is a significant auth event (not just a token refresh)
        if (throttleInitialization(user)) {
            console.log('Starting app initialization sequence')
            await initializeApp(user)
        } else {
            console.log('Skipping redundant initialization')
        }
    } catch (error) {
        console.error('Error in onAuthStateChanged handler:', error)

        // If no user, ensure cleanup and redirect
        if (!user) {
            cleanupAppSockets()

            // Skip redirect for noAuth routes even in error case
            if (router.currentRoute.value.meta.noAuth) {
                console.log(
                    '%c AUTH DEBUG: On noAuth page (error case), NOT redirecting',
                    'background: #900; color: #fff'
                )
                return
            }

            initState.value.initialized = false
            authStore.setAppInitialized(false)

            // Redirect to auth page even if there was an error
            router.push({ name: 'auth' })
        }
    }
})

// Computed property to control BuyStarsModal visibility
const showBuyStarsModal = computed({
    get: () =>
        paymentStore.state.showBuyStarsModal &&
        !paymentStore.isPaymentInProgress(),
    set: (value) => {
        // Only allow setting to true if no payment is in progress
        if (value && paymentStore.isPaymentInProgress()) {
            console.log(
                'Payment in progress, preventing buy stars modal from opening'
            )
            return
        }
        paymentStore.state.showBuyStarsModal = value
    },
})
</script>
